import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import * as Icons from "lucide-react";
import { useMode } from "../context/ModeContext";
import { ModeToggle } from "../components/ModeToggle";
import { DevicePriceWizardV2 } from "../components/DevicePriceWizardV2";
import { PremiumCard } from "../components/PremiumCard";
import { PremiumButton } from "../components/PremiumButton";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { fadeIn, slideUp, staggerContainer, floatingDevice } from "../animations";

export const HomeAppleStyle = () => {
  const { mode, setMode } = useMode();
  const { scrollY } = useScroll();
  
  // Subtle parallax effects - reduced movement and opacity
  const heroY = useTransform(scrollY, [0, 600], [0, 100]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0.4]);
  const deviceY = useTransform(scrollY, [0, 600], [0, -50]); // Devices move slower (opposite direction)
  const deviceOpacity = useTransform(scrollY, [0, 500], [1, 0.2]); // Devices fade out smoothly

  const [refFeatures, isFeaturesVisible] = useScrollReveal({ once: true });
  const [refProcess, isProcessVisible] = useScrollReveal({ once: true });
  const [refCTA, isCTAVisible] = useScrollReveal({ once: true });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      {/* Mode Toggle */}
      <ModeToggle mode={mode} onModeChange={setMode} />

      {/* Hero Section - Apple Style */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50" />
        
        {/* Animated Gradient Orbs */}
        <motion.div
          className="absolute top-20 -left-40 w-96 h-96 bg-gradient-to-r from-[#1f7db6]/20 to-purple-400/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-20 -right-40 w-96 h-96 bg-gradient-to-l from-[#143e6e]/20 to-blue-400/20 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.5, 0.3, 0.5],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />

        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-5xl mx-auto"
            style={{ y: heroY, opacity: heroOpacity }}
          >
            {/* Headline */}
            <motion.h1 
              variants={slideUp}
              className="text-6xl sm:text-7xl lg:text-8xl font-bold mb-8 leading-tight"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#143e6e] via-[#1f7db6] to-[#143e6e]">
                {mode === 'b2b' 
                  ? 'Firmenlaptops sicher verkaufen'
                  : 'Laptop oder Smartphone verkaufen'}
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p 
              variants={slideUp}
              className="text-xl sm:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed"
            >
              {mode === 'b2b'
                ? 'B2B-Ankauf mit zertifizierter Datenlöschung und strukturierter Logistik'
                : 'Erhalten Sie schnell ein faires Sofortangebot für Ihr gebrauchtes Gerät'}
            </motion.p>

            {/* CTA */}
            <motion.div variants={slideUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              {mode === 'b2b' ? (
                <Link to="/request-quote">
                  <PremiumButton 
                    variant="primary" 
                    size="lg"
                    icon={Icons.ArrowRight}
                  >
                    Firmenangebot anfordern
                  </PremiumButton>
                </Link>
              ) : (
                <span
                  onClick={() => {
                    const wizardElement = document.getElementById('price-wizard');
                    if (wizardElement) {
                      wizardElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="inline-block cursor-pointer"
                  data-testid="hero-cta-private"
                >
                  <PremiumButton 
                    variant="primary" 
                    size="lg"
                    icon={Icons.ArrowRight}
                  >
                    Jetzt Gerät anbieten
                  </PremiumButton>
                </span>
              )}
            </motion.div>

            {/* Trust Indicators */}
            <motion.div 
              variants={fadeIn}
              className="mt-16 flex flex-wrap justify-center gap-8 text-sm text-gray-600"
            >
              <div className="flex items-center gap-2">
                <Icons.ShieldCheck className="text-[#1f7db6]" size={20} />
                <span>{mode === 'b2b' ? 'DSGVO-konform' : 'Sichere Datenlöschung'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icons.Zap className="text-[#1f7db6]" size={20} />
                <span>{mode === 'b2b' ? '48h Erstprüfung' : 'Sofortangebot'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icons.Euro className="text-[#1f7db6]" size={20} />
                <span>Faire Preise</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Floating Device Mockup - Enhanced & Fully Visible with Soft Fade - Hidden on Mobile */}
          <motion.div
            className="mt-24 mb-16 relative hidden md:block"
            style={{ y: deviceY, opacity: deviceOpacity }}
          >
            <div className="relative mx-auto w-full max-w-5xl h-[500px] flex items-center justify-center">
              {/* Laptop - Left side, fully visible */}
              <motion.div
                initial={{ opacity: 0, x: -100, rotateY: -15 }}
                animate={{ 
                  opacity: 1, 
                  x: 0,
                  rotateY: 0,
                }}
                transition={{ 
                  duration: 1.2, 
                  delay: 0.3,
                  ease: "easeOut"
                }}
                whileHover={{ 
                  scale: 1.05,
                  rotateY: 5,
                  transition: { duration: 0.3 }
                }}
                className="absolute left-[5%] top-1/2 -translate-y-1/2 w-[420px] h-[280px] z-10"
                style={{ 
                  transformStyle: "preserve-3d",
                  perspective: "1000px"
                }}
              >
                {/* Laptop outer shell */}
                <div className="relative w-full h-full bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-2xl shadow-2xl transform -rotate-3">
                  {/* Screen bezel */}
                  <div className="absolute inset-3 bg-gradient-to-br from-gray-700 to-gray-800 rounded-xl overflow-hidden">
                    {/* Screen with gradient glow */}
                    <div className="absolute inset-2 bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-blue-600/20 rounded-lg">
                      {/* Screen content simulation */}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#143e6e]/40 to-[#1f7db6]/40 backdrop-blur-sm" />
                      {/* Glowing effect */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"
                        animate={{
                          opacity: [0.3, 0.6, 0.3],
                        }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      />
                    </div>
                  </div>
                  {/* Camera notch */}
                  <div className="absolute top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-950 rounded-full border border-gray-700" />
                  {/* Keyboard base hint */}
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-full h-3 bg-gradient-to-b from-gray-800 to-gray-900 rounded-b-2xl" />
                </div>
                {/* Laptop shadow */}
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-[90%] h-4 bg-black/20 blur-xl rounded-full" />
              </motion.div>
              
              {/* Smartphone - Right side, fully visible */}
              <motion.div
                initial={{ opacity: 0, x: 100, rotateZ: 15 }}
                animate={{ 
                  opacity: 1, 
                  x: 0,
                  rotateZ: 0,
                }}
                transition={{ 
                  duration: 1.2, 
                  delay: 0.5,
                  ease: "easeOut"
                }}
                whileHover={{ 
                  scale: 1.08,
                  rotateZ: -3,
                  transition: { duration: 0.3 }
                }}
                className="absolute right-[8%] top-1/2 -translate-y-1/2 w-[180px] h-[360px] z-20"
              >
                {/* Phone outer shell */}
                <div className="relative w-full h-full bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-[2.5rem] shadow-2xl transform rotate-6 border-4 border-gray-950">
                  {/* Screen bezel */}
                  <div className="absolute inset-2 bg-gradient-to-br from-gray-700 to-gray-800 rounded-[2rem] overflow-hidden">
                    {/* Screen with gradient */}
                    <div className="absolute inset-1 bg-gradient-to-br from-purple-500/20 via-blue-500/20 to-purple-600/20 rounded-[1.8rem]">
                      {/* Screen content */}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1f7db6]/40 to-[#143e6e]/40 backdrop-blur-sm" />
                      {/* Glowing effect */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"
                        animate={{
                          opacity: [0.4, 0.7, 0.4],
                        }}
                        transition={{
                          duration: 2.5,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: 0.5
                        }}
                      />
                    </div>
                  </div>
                  {/* Dynamic Island / Notch */}
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-full border border-gray-800" />
                  {/* Side buttons */}
                  <div className="absolute right-0 top-24 w-1 h-12 bg-gray-950 rounded-l" />
                  <div className="absolute right-0 top-40 w-1 h-8 bg-gray-950 rounded-l" />
                  <div className="absolute left-0 top-32 w-1 h-16 bg-gray-950 rounded-r" />
                </div>
                {/* Phone shadow */}
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-[70%] h-4 bg-black/20 blur-xl rounded-full" />
              </motion.div>
              
              {/* Ambient light effects */}
              <motion.div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-full blur-3xl -z-10"
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.5, 0.3],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Icons.ChevronDown className="text-gray-400" size={32} />
        </motion.div>
      </section>

      {/* C2B: Price Wizard Section */}
      {mode === 'c2b' && (
        <section id="price-wizard" className="py-32 relative scroll-mt-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={staggerContainer}
              className="text-center mb-16"
            >
              <motion.h2 
                variants={slideUp}
                className="text-5xl sm:text-6xl font-bold text-[#143e6e] mb-6"
              >
                Jetzt Gerät bewerten lassen
              </motion.h2>
              <motion.p 
                variants={slideUp}
                className="text-xl text-gray-600 max-w-2xl mx-auto"
              >
                In wenigen Schritten zum Sofortangebot
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <DevicePriceWizardV2 />
            </motion.div>
          </div>
        </section>
      )}

      {/* B2B: Features Section */}
      {mode === 'b2b' && (
        <section ref={refFeatures} className="py-32 relative">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              animate={isFeaturesVisible ? "visible" : "hidden"}
              variants={staggerContainer}
              className="text-center mb-20"
            >
              <motion.h2 
                variants={slideUp}
                className="text-5xl sm:text-6xl font-bold text-[#143e6e] mb-6"
              >
                Vorteile für Unternehmen
              </motion.h2>
            </motion.div>

            <motion.div 
              initial="hidden"
              animate={isFeaturesVisible ? "visible" : "hidden"}
              variants={staggerContainer}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            >
              {[
                { icon: Icons.ShieldCheck, title: "Zertifizierte Datenlöschung", desc: "DSGVO-konform mit Zertifikat" },
                { icon: Icons.Truck, title: "Strukturierte Logistik", desc: "Deutschlandweite Abholung" },
                { icon: Icons.Clock, title: "Schnelle Bewertung", desc: "48h Erstprüfung" },
                { icon: Icons.Package, title: "Großmengen", desc: "Ab 20 Geräten" }
              ].map((feature, idx) => (
                <motion.div key={idx} variants={slideUp}>
                  <PremiumCard className="p-8 text-center h-full">
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: "spring", stiffness: 300 }}
                      className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-[#1f7db6] to-[#143e6e] rounded-2xl flex items-center justify-center"
                    >
                      <feature.icon className="text-white" size={32} />
                    </motion.div>
                    <h3 className="font-bold text-xl mb-3 text-[#143e6e]">{feature.title}</h3>
                    <p className="text-gray-600">{feature.desc}</p>
                  </PremiumCard>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section ref={refCTA} className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#143e6e] via-[#1f7db6] to-[#143e6e]" />
        
        <motion.div
          initial="hidden"
          animate={isCTAVisible ? "visible" : "hidden"}
          variants={staggerContainer}
          className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          <motion.h2 
            variants={slideUp}
            className="text-5xl sm:text-6xl font-bold text-white mb-6"
          >
            {mode === 'b2b' ? 'Möchten Sie Firmenlaptops verkaufen?' : 'Bereit zum Verkauf?'}
          </motion.h2>
          
          <motion.p 
            variants={slideUp}
            className="text-xl text-white/90 mb-12 max-w-2xl mx-auto"
          >
            {mode === 'b2b' 
              ? 'Fordern Sie jetzt Ihr unverbindliches Angebot an'
              : 'Starten Sie jetzt und erhalten Sie Ihr Sofortangebot'}
          </motion.p>

          <motion.div variants={slideUp}>
            {mode === 'b2b' ? (
              <Link to="/request-quote">
                <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                  Firmenangebot anfordern
                </PremiumButton>
              </Link>
            ) : (
              <span
                onClick={() => {
                  const wizardElement = document.getElementById('price-wizard');
                  if (wizardElement) {
                    wizardElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }}
                className="inline-block cursor-pointer"
                data-testid="bottom-cta-private"
              >
                <PremiumButton variant="secondary" size="lg" icon={Icons.ArrowRight}>
                  Jetzt Gerät anbieten
                </PremiumButton>
              </span>
            )}
          </motion.div>
        </motion.div>

        {/* Decorative elements */}
        <motion.div
          className="absolute -bottom-20 -left-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
          transition={{ duration: 8, repeat: Infinity }}
        />
        <motion.div
          className="absolute -top-20 -right-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.2, 0.1, 0.2] }}
          transition={{ duration: 8, repeat: Infinity, delay: 1 }}
        />
      </section>
    </div>
  );
};
